import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type JobLayoutCard } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { iconButton } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { orderAfterMove } from '../servicetypes/ServiceTypesSettingsCard';
import { IconChevronDown, IconChevronUp, IconJobs } from '../icons';

export const JOB_CARD_LABELS: Record<JobLayoutCard, string> = {
  details: 'Job details',
  customer: 'Customer',
  property: 'Service property',
  quotes: 'Quotes',
  work_orders: 'Work orders',
  attachments: 'Attachments',
  custom_fields: 'Custom fields',
};

/** Predefined cards only; the same editor serves shop defaults and personal preferences. */
export function JobCardLayoutEditor({
  cardOrder,
  hiddenCards,
  locked,
  onChange,
}: {
  cardOrder: JobLayoutCard[];
  hiddenCards: JobLayoutCard[];
  locked: boolean;
  onChange: (next: { cardOrder: JobLayoutCard[]; hiddenCards: JobLayoutCard[] }) => void;
}) {
  return (
    <ol className="divide-y divide-border">
      {cardOrder.map((card, index) => (
        <li key={card} className="flex min-h-12 items-center gap-2 py-2">
          <label className="mr-auto flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!hiddenCards.includes(card)}
              disabled={locked}
              onChange={(event) =>
                onChange({
                  cardOrder,
                  hiddenCards: event.target.checked
                    ? hiddenCards.filter((value) => value !== card)
                    : [...hiddenCards, card],
                })
              }
            />
            {JOB_CARD_LABELS[card]}
          </label>
          {([-1, 1] as const).map((direction) => (
            <button
              key={direction}
              type="button"
              className={iconButton}
              disabled={
                locked ||
                (direction === -1 ? index === 0 : index === cardOrder.length - 1)
              }
              aria-label={`Move ${JOB_CARD_LABELS[card]} ${direction === -1 ? 'up' : 'down'}`}
              onClick={() => {
                const next = orderAfterMove(cardOrder, card, direction);
                if (next) onChange({ cardOrder: next as JobLayoutCard[], hiddenCards });
              }}
            >
              {direction === -1 ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </button>
          ))}
        </li>
      ))}
    </ol>
  );
}

export function JobLayoutSettingsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const mayManage = can(session.role, 'jobPipeline', 'manage');
  const online = useOnline();
  const q = useQuery({ ...trpc.commercialJob.layout.queryOptions(), enabled: mayManage });
  const update = useMutation(
    trpc.commercialJob.updateLayout.mutationOptions({
      onSuccess: (layout) => {
        qc.setQueryData(trpc.commercialJob.layout.queryKey(), layout);
        void qc.invalidateQueries({ queryKey: trpc.userPrefs.jobView.pathKey() });
      },
    }),
  );
  const state = shownState(q, q.data);
  return (
    <SectionCard id={id} title="Job workspace cards">
      <p className="mb-3 text-sm text-muted-foreground">
        Set the default visibility and order of predefined Job sections. Personal choices
        can further hide cards; they cannot restore a section hidden for the shop.
      </p>
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {update.error ? (
        <Notice tone="danger" alert>
          {update.error.message}
        </Notice>
      ) : null}
      {!mayManage ? (
        <Notice tone="warn">You do not have access to Job workspace settings.</Notice>
      ) : state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconJobs}
          what="Job workspace settings"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data ? (
        <JobCardLayoutEditor
          cardOrder={q.data.cardOrder as JobLayoutCard[]}
          hiddenCards={q.data.hiddenCards as JobLayoutCard[]}
          locked={!online || q.isFetching || q.isError || update.isPending}
          onChange={(next) =>
            update.mutate({ ...next, expectedVersion: q.data.version, ...idem() })
          }
        />
      ) : null}
    </SectionCard>
  );
}
